import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, X, Copy } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from './adminConstants';

const LABELS = {
  nome_empresa: 'Nome da empresa',
  nome_responsavel: 'Nome do responsável',
  email_empresa: 'E-mail da empresa',
  email_automatico: 'E-mail automático',
  documento: 'CPF/CNPJ',
  whatsapp_disparo: 'WhatsApp de disparo',
  logo: 'Logomarca',
  google: 'Link do Google',
  cidade: 'Cidade',
  estado: 'Estado',
};

const AdminIncompleteDataModal = ({ onClose }) => {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campo, setCampo] = useState('todos');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/companies/incomplete`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setData(res.data);
    } catch { toast.error('Erro ao carregar os cadastros incompletos'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.companies || []).filter(c => campo === 'todos' || c.missing.includes(campo));

  const copyCsv = () => {
    const head = 'Empresa;Plano;Responsavel;E-mail;WhatsApp;Completude;Faltando\n';
    const body = rows.map(c => [
      c.name, c.plan_name, c.owner_name, c.owner_email, c.whatsapp, `${c.completude}%`,
      c.missing.map(m => LABELS[m]).join(' | '),
    ].join(';')).join('\n');
    navigator.clipboard.writeText(head + body);
    toast.success(`${rows.length} linha(s) copiada(s) — cole no Excel`);
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" data-testid="incomplete-modal">
      <Card className="w-full max-w-5xl max-h-[88vh] overflow-hidden flex flex-col">
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Cadastros com dado faltando
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              {data ? `${data.incompletas} de ${data.total_ativas} empresas ativas têm algum dado de configuração em falta.` : 'Carregando...'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600" data-testid="close-incomplete-modal"><X className="w-5 h-5" /></button>
        </CardHeader>

        {data && (
          <div className="px-6 pb-3 flex flex-wrap gap-2 border-b">
            <button
              onClick={() => setCampo('todos')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${campo === 'todos' ? 'bg-[#0A2647] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="incomplete-filter-todos"
            >
              Todas ({data.incompletas})
            </button>
            {Object.entries(LABELS).map(([k, label]) => (
              data.counts[k] > 0 && (
                <button
                  key={k}
                  onClick={() => setCampo(k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${campo === k ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                  data-testid={`incomplete-filter-${k}`}
                >
                  {label} ({data.counts[k]})
                </button>
              )
            ))}
          </div>
        )}

        <CardContent className="overflow-y-auto flex-1 pt-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">Nenhuma empresa nesta lista. 🎉</p>
          ) : (
            <div className="space-y-2">
              {rows.map(c => (
                <div key={c.id} className="p-3 rounded-lg border border-slate-200" data-testid={`incomplete-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">
                        {c.name || '(sem nome)'}
                        <span className="ml-2 text-xs font-normal text-slate-400">{c.plan_name}</span>
                        {c.is_test && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">TESTE</span>}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.owner_name || '(sem responsável)'} · {c.owner_email || 'sem e-mail'} · {c.whatsapp || 'sem WhatsApp'}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {c.missing.map(m => (
                          <span key={m} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            {LABELS[m]}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-lg font-bold ${c.completude >= 70 ? 'text-emerald-600' : c.completude >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                        {c.completude}%
                      </p>
                      <p className="text-[10px] text-slate-400">completo</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>

        <div className="border-t p-4 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">{rows.length} empresa(s) na lista</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyCsv} disabled={!rows.length} data-testid="incomplete-copy-csv">
              <Copy className="w-4 h-4 mr-2" />Copiar para Excel
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="incomplete-close-btn">Fechar</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminIncompleteDataModal;
